/**
 * SB2-CC-01 — single FE source of role truth.
 *
 * Reads from `<RoleMatrixContextProvider>` (populated by
 * `RoleContext.tsx`). Every component that wants to render a
 * role-dependent UI flag goes through this hook OR
 * `useCanEditResource` — direct reads of
 * `useAuthStore(s => s.session.user.roles)` for decision-making are a
 * SB2-CC-01 violation (AC9: `grep -rn "role ===" apps/web/src
 * packages/ui/src` returns nothing outside `RoleContext.tsx` /
 * `useCurrentUserRole.ts`).
 *
 * Returns `{ role: string | null }` — the canonical role from the
 * role-matrix-context. `role === null` means "matrix still loading"
 * (treat as no-permission until it resolves).
 */
import { useRoleMatrixContext, type Role } from "@aeriosiq/ui";

export interface UseCurrentUserRoleApi {
  role: Role | null;
}

export function useCurrentUserRole(): UseCurrentUserRoleApi {
  const { role } = useRoleMatrixContext();
  return { role };
}

/**
 * Convenience predicate: `useHasRole("tenant_admin")` returns `true`
 * iff the caller's resolved role is the given one. This is the one
 * approved escape hatch for the small set of UI decisions that depend
 * on the **role identity** rather than a **permission key** (e.g. the
 * Edit-Turbine modal's tenant-admin-only Park field — there is no
 * `turbine.update_park` permission key in the matrix yet, so the role
 * check is the unavoidable surface).
 *
 * The AC9 sweep (`grep -rn "role ===" apps/web/src packages/ui/src`)
 * is the regression gate for inline `role === "..."` checks; consumers
 * route through THIS helper instead so the check happens inside the
 * allowed file (`useCurrentUserRole.ts`).
 */
export function useHasRole(target: Role): boolean {
  const { role } = useRoleMatrixContext();
  return role === target;
}
